import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
   CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CFormCheck, CCardFooter, CSpinner
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import ToastNotification from "components/globals/ToastNotification";
import UserSelectionModal from "components/UserSelectionModal";
import DateInput from "components/forms/DateInput";
import TextEditor from 'components/forms/TextEditor';
import { triggerAlert } from 'components/globals/AlertNotification';

const ImprovementAction = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const [formData, setFormData] = useState(null);
   const location = useLocation();
   const [users, setUsers] = useState([]);
   const [toast, setToast] = useState(null);
   const [showModalResponsible, setShowModalResponsible] = useState(false);
   const [showModalParticipants, setShowModalParticipants] = useState(false);
   const [isEditing, setIsEditing] = useState(true);
   const [loading, setLoading] = useState(false);

   const token = localStorage.getItem("access_token");
   const decodedToken = jwtDecode(token);
   const is_director = decodedToken.is_director;
   const user_id = decodedToken.user_id;

   const fetchImprovementActionDetails = async () => {
      let data = null; // Declare a variable to store API response

      try {
         const response = await apiClient.get(`/improvement-actions/${id}/`);
         data = response.data; // Store response data
         setFormData(data); // Update state
      } catch (error) {
         triggerAlert('Ação de Melhoria não foi encontrada!', 'error');
         navigate("/acoes-melhoria");
      } finally {
         setIsEditing(data?.closed !== true); // Use the local variable, NOT formData
      }
   };

   useEffect(() => {
      const fetchUsers = async () => {
         try {
            let response;
            if (formData !== null && formData.approved != null && (formData.responsible_members.length > 0 || formData.other_participants.length > 0)) {
               response = await apiClient.get('/users/by-ids/', {
                  params: { ids: [...formData.responsible_members, ...formData.other_participants] },
               });
            } else {
               response = await apiClient.get('/users/active/');
            }
            setUsers(response.data);
         } catch (error) {
            console.error('Error fetching users:', error);
         }
      };

      fetchImprovementActionDetails();
      fetchUsers();

   }, [id, location.pathname]);

   const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      let updatedValue;

      if (type === "checkbox") {
         updatedValue = checked; // true if checked, false if unchecked
      } else if (value === "true") {
         updatedValue = true;
      } else if (value === "false") {
         updatedValue = false;
      } else {
         updatedValue = value || null; // Convert empty values to null
      }

      setFormData((prevFormData) => {
         let updatedForm = {
            ...prevFormData,
            [name]: updatedValue,
         };

         return updatedForm;
      });
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
         if (id) {
            await apiClient.put(`/improvement-actions/${id}/`, formData);
            triggerAlert('Ação de Melhoria atualizada com sucesso!', 'success');
            navigate(`/acoes-melhoria`);
         } else {
            await apiClient.post('/improvement-actions/', formData);
            triggerAlert('Ação de Melhoria atualizada com sucesso!', 'success');
            navigate('/acoes-melhoria');
         }
      } catch (error) {
         const errorMessage = error.response?.data?.error || 'Erro ao gravar';
         setToast({ title: 'Ação de Melhoria', message: { errorMessage }, type: 'error' });
         console.error('Error submitting form:', error);
         setLoading(false);
      }
   };

   const handleCheckboxUsersChangeResponsibles = (userId, isChecked) => {
      setFormData((prevData) => {
         let updatedMembers = isChecked
            ? [...prevData.responsible_members, userId]
            : prevData.responsible_members.filter((id) => id !== userId);

         return { ...prevData, responsible_members: updatedMembers };
      });
   };

   const handleCheckboxUsersChangeParticipants = (userId, isChecked) => {
      setFormData((prevData) => {
         let updatedMembers = isChecked
            ? [...prevData.other_participants, userId]
            : prevData.other_participants.filter((id) => id !== userId);

         return { ...prevData, other_participants: updatedMembers };
      });
   };

   if (formData !== null) {
      if (!isEditing || (!formData.responsible_members.includes(user_id) && !is_director)) {
         const renderText = (text) => {
            return <div dangerouslySetInnerHTML={{ __html: text || "-" }} />;
         };

         return (
            <div className="container mt-4">
               <h4 className="text-center mb-5">Detalhes da Ação de Melhoria</h4>
               {[
                  { label: "Código", value: formData.code },
                  { label: "Tipo de Ação", value: formData.action_type || "-" },
                  { label: "Fonte", value: formData.source },
                  { label: "Descrição (OM)", value: renderText(formData.improvement_opp_description) },
                  {
                     label: "Natureza",
                     value: (
                        <>
                           {formData.organizational && <div>Organizacional</div>}
                           {formData.service && <div>Serviço</div>}
                           {formData.marketing && <div>Marketing</div>}
                           {formData.process && <div>Processo</div>}
                        </>
                     )
                  },
                  { label: "Causas", value: renderText(formData.causes) },
                  { label: "Soluções", value: renderText(formData.solutions) },
                  {
                     label: "Responsáveis",
                     value: formData.responsible_members.length > 0 ? (
                        <ul>
                           {users
                              .filter((user) => formData.responsible_members.includes(user.id))
                              .map((user) => (
                                 <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                              ))}
                        </ul>
                     ) : (
                        <p className="text-muted">Nenhum responsável selecionado</p>
                     )
                  },
                  {
                     label: "Participantes",
                     value: formData.other_participants.length > 0 ? (
                        <ul>
                           {users
                              .filter((user) => formData.other_participants.includes(user.id))
                              .map((user) => (
                                 <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                              ))}
                        </ul>
                     ) : (
                        <p className="text-muted">Nenhum participante selecionado</p>
                     )
                  },
                  { label: "Prazo", value: formData.deadline },
                  { label: "Ação", value: renderText(formData.action) },
                  { label: "Data de Conclusão", value: formData.conclusion_date || "Por concluir" },
                  { label: "Eficácia da Ação", value: formData.efficiency },
                  { label: "Motivo de Ineficácia", value: renderText(formData.inefficiency_reasons) },
                  { label: "Observações", value: renderText(formData.obs) }
               ].map((item, index) => (
                  <CCard key={index} className="mb-3">
                     <CCardHeader>
                        <h5>{item.label}</h5>
                     </CCardHeader>
                     <CCardBody>
                        <div>{item.value || ""}</div>
                     </CCardBody>
                  </CCard>
               ))}

               {/* Back Button */}
               <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
                  <CButton color="danger" className="text-white" onClick={() => navigate('/acoes-melhoria')}>
                     Voltar
                  </CButton>
               </div>
            </div>
         );
      }

      return (
         <div className="container mt-4">
            {/* Error Toast - Appears on submit failure */}
            <ToastNotification toast={toast} setToast={setToast} />

            <CCard>
               <CCardHeader>
                  <h3>{id ? 'Editar Ação de Melhoria' : 'Nova Ação de Melhoria'}</h3>
               </CCardHeader>
               <CCardBody>
                  <CForm onSubmit={handleSubmit}>
                     <CFormInput
                        type="text"
                        label={<strong>Código</strong>}
                        name="code"
                        value={formData.code}
                        readOnly
                        plainText
                        className="mb-3"
                     />

                     <CFormSelect
                        label={<strong>Tipo de Ação</strong>}
                        name="action_type"
                        value={formData.action_type ?? ''}
                        onChange={handleInputChange}
                        className="mb-3"
                     >
                        <option value="">Selecione</option>
                        <option value="Corretiva">Corretiva</option>
                        <option value="Melhoria">Melhoria</option>
                     </CFormSelect>

                     <CFormInput
                        type="text"
                        label={<strong>Fonte</strong>}
                        name="code"
                        value={formData.opportunity.idea !== null ? "Ideia/Sugestão" : "Não Conformidade"}
                        readOnly
                        plainText
                        className="mb-3"
                     />

                     <strong>Descrição (OM)</strong>
                     <div
                        className="mb-3"
                        dangerouslySetInnerHTML={{ __html: formData.improvement_opp_description ?? '' }}
                     />

                     <div className="mt-3 mb-3">
                        <div><strong>Natureza</strong></div>
                        {[
                           ["organizational", "Organizacional"],
                           ["service", "Serviço"],
                           ["marketing", "Marketing"],
                           ["process", "Processo"]
                        ].map(([key, label]) => (
                           <CFormCheck
                              key={key}
                              label={label}
                              name={key}
                              checked={formData[key]}
                              onChange={handleInputChange}
                           />
                        ))}
                     </div>

                     <TextEditor
                        label="Causas"
                        value={formData.causes ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "causes", value } })
                        }
                        className="mb-3"
                     />

                     <TextEditor
                        label="Solucões"
                        value={formData.solutions ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "solutions", value } })
                        }
                        className="mb-3"
                     />

                     <div className="mt-3 mb-3">
                        <div><strong>NC é generalizável? Tem implicações Risco/SGQ</strong></div>
                        <CFormCheck
                           name="generalizable"
                           checked={formData.generalizable}
                           onChange={handleInputChange}
                        />
                     </div>

                     <TextEditor
                        label="Ação"
                        value={formData.action ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "action", value } })
                        }
                        className="mb-3"
                     />

                     <div className="d-flex justify-content-between mt-3">
                        <div className="w-50 pe-2">
                           <CButton color="info" className="text-white mb-2" onClick={() => setShowModalResponsible(true)}>
                              Selecionar Responsáveis
                           </CButton>
                           {formData.responsible_members.length > 0 ? (
                              <ul>
                                 {users
                                    .filter((user) => formData.responsible_members.includes(user.id))
                                    .map((user) => (
                                       <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                                    ))}
                              </ul>
                           ) : (
                              <p className="text-muted">Nenhum responsável selecionado</p>
                           )}
                        </div>

                        <div className="w-50 ps-2">
                           <CButton color="info" className="text-white mb-2" onClick={() => setShowModalParticipants(true)}>
                              Selecionar Outros Participantes
                           </CButton>
                           {formData.other_participants.length > 0 ? (
                              <ul>
                                 {users
                                    .filter((user) => formData.other_participants.includes(user.id))
                                    .map((user) => (
                                       <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                                    ))}
                              </ul>
                           ) : (
                              <p className="text-muted">Nenhum participante selecionado</p>
                           )}
                        </div>
                     </div>

                     <DateInput
                        label={<strong>Prazo</strong>}
                        name="deadline"
                        value={formData.deadline ?? ''}
                        onChange={handleInputChange}
                     />

                     {is_director && (
                        <CFormSelect
                           label={<strong>Aprovado</strong>}
                           name="approved"
                           value={formData.approved ?? ''}
                           onChange={handleInputChange}
                           className="mb-3"
                        >
                           <option value="">Selecione</option>
                           <option value="true">Sim</option>
                           <option value="false">Não</option>
                        </CFormSelect>
                     )}


                     <CFormInput
                        type="text"
                        label={<strong>Data de Conclusão</strong>}
                        name="code"
                        value={formData.conclusion_date ?? 'Por concluir'}
                        readOnly
                        plainText
                        className="mb-3"
                     />

                     {is_director && (
                        <>
                           <strong className="mb-2 d-block">Avaliação</strong>
                           <DateInput
                              label={<strong>Data da Avaliação</strong>}
                              name="evaluation_date"
                              value={formData.evaluation_date ?? ''}
                              onChange={handleInputChange}
                           />
                           
                           <CFormSelect
                              label={<strong>Eficácia da Ação</strong>}
                              name="efficiency"
                              value={formData.efficiency ?? ''}
                              onChange={handleInputChange}
                              className="mb-3"
                           >
                              <option value="">Selecione</option>
                              <option value="Eficaz">Eficaz</option>
                              <option value="Ineficaz">Ineficaz</option>
                              <option value="Fechada s/ avaliação">Fechada s/ avaliação</option>
                              <option value="Em curso">Em curso</option>
                           </CFormSelect>

                           <TextEditor
                              label="Em caso de ineficácia, indicar motivo:"
                              value={formData.inefficiency_reasons ?? ''}
                              onChange={(value) =>
                                 handleInputChange({ target: { name: "inefficiency_reasons", value } })
                              }
                              className="mb-3"
                           />

                           <TextEditor
                              label="Observações"
                              value={formData.obs ?? ''}
                              onChange={(value) =>
                                 handleInputChange({ target: { name: "obs", value } })
                              }
                              className="mb-3"
                           />
                        </>
                     )}

                     <UserSelectionModal
                        title="Selecionar Responsáveis"
                        visible={showModalResponsible}
                        onClose={() => setShowModalResponsible(false)}
                        users={users}
                        selectedUsers={formData.responsible_members}
                        onChange={handleCheckboxUsersChangeResponsibles}
                     />

                     <UserSelectionModal
                        title="Selecionar Participantes"
                        visible={showModalParticipants}
                        onClose={() => setShowModalParticipants(false)}
                        users={users}
                        selectedUsers={formData.other_participants}
                        onChange={handleCheckboxUsersChangeParticipants}
                     />
                  </CForm>
               </CCardBody>
               <CCardFooter className="d-flex justify-content-end gap-2">
                  <CButton color="danger" className="text-white" onClick={() => navigate("/acoes-melhoria")}>
                     Cancelar
                  </CButton>
                  <CButton disabled={loading} color="success" className="text-white" type="submit" onClick={handleSubmit}>
                     {loading ? <CSpinner size="sm" /> : 'Confirmar'}
                  </CButton>
               </CCardFooter>
            </CCard>
         </div>
      );
   }

   return (
      <div className="container mt-4">
         <h2>Loading...</h2>
      </div>
   );
};

export default ImprovementAction;